import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Clock, 
  Calendar, 
  CheckCircle2, 
  MapPin,
  Phone,
  ArrowRight,
  Users,
  Car,
  Award,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useFAQSchema, commonFAQs } from '@/utils/aiSeoSchemas';
import usePageContent from '@/hooks/usePageContent';

const IntensivkursPage = () => {
  const { t: c } = usePageContent('intensivkurs');
  // AI-optimized FAQ Schema for Intensivkurs
  useFAQSchema([...commonFAQs.general.slice(0, 3), ...commonFAQs.intensivkurs]);

  useEffect(() => {
    // SEO Meta Tags
    document.title = 'Intensivkurs Führerschein Frankfurt, Offenbach & Bad Homburg | 2-4 Wochen | Fahrschule Rößner';
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Führerschein Intensivkurs in Frankfurt, Offenbach, Bad Homburg, Niederrad & Schwanheim. In 2-4 Wochen zum Führerschein Klasse B. Flexible Termine, erfahrene Fahrlehrer. Jetzt anmelden!');
    }

    // Add Schema.org markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Führerschein Intensivkurs Klasse B",
      "description": "Kompakter Führerschein-Intensivkurs in 2-4 Wochen. Tägliche Fahrstunden und Theorieunterricht für schnellen Lernerfolg.",
      "provider": {
        "@type": "DrivingSchool",
        "name": "Fahrschule Rößner",
        "url": "https://www.roessnerfahrschule.de"
      },
      "courseMode": "onsite",
      "educationalLevel": "Beginner",
      "teaches": "Führerschein Klasse B (PKW)",
      "hasCourseInstance": [
        {
          "@type": "CourseInstance",
          "courseMode": "onsite",
          "location": {
            "@type": "Place",
            "name": "Fahrschule Rößner Frankfurt Schwanheim",
            "address": "Alt-Schwanheim 29, 60529 Frankfurt am Main"
          }
        }
      ]
    };

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schema);
    scriptTag.id = 'intensivkurs-schema';
    document.head.appendChild(scriptTag);

    return () => {
      const existingScript = document.getElementById('intensivkurs-schema');
      if (existingScript) existingScript.remove();
    };
  }, []);

  const standorte = [
    {
      name: 'Frankfurt Schwanheim',
      slug: 'frankfurt-schwanheim',
      address: 'Alt-Schwanheim 29, 60529 Frankfurt am Main',
      phone: '069 20 32 60 59',
      keywords: ['Intensivkurs Frankfurt', 'Intensivkurs Schwanheim', 'Fahrschule Frankfurt Schwanheim']
    },
    {
      name: 'Frankfurt Niederrad',
      slug: 'frankfurt-niederrad', 
      address: 'Bruchfeldstraße 120, 60528 Frankfurt am Main',
      phone: '069 67 86 46 57',
      keywords: ['Intensivkurs Niederrad', 'Fahrschule Frankfurt Niederrad', 'Führerschein Niederrad']
    },
    {
      name: 'Offenbach am Main',
      slug: 'offenbach',
      address: 'Waldstraße 111, 63071 Offenbach am Main',
      phone: '069 82 36 98 14',
      keywords: ['Intensivkurs Offenbach', 'Fahrschule Offenbach', 'Führerschein Offenbach']
    },
    {
      name: 'Bad Homburg',
      slug: 'bad-homburg',
      address: 'Louisenstraße 2, 61348 Bad Homburg vor der Höhe',
      phone: '06172 92 17 58',
      keywords: ['Intensivkurs Bad Homburg', 'Fahrschule Bad Homburg', 'Intensivkurs Oberursel']
    }
  ];

  const vorteile = [
    {
      icon: Zap,
      title: c('vorteil1_title', 'Schnell zum Führerschein'),
      description: c('vorteil1_desc', 'In nur 2-4 Wochen zum Führerschein Klasse B - ideal für Schüler, Studenten und Berufstätige.')
    },
    {
      icon: Calendar,
      title: c('vorteil2_title', 'Flexible Terminplanung'),
      description: c('vorteil2_desc', 'Wir passen den Intensivkurs an Ihre Verfügbarkeit an - auch in den Ferien oder bei Urlaub.')
    },
    {
      icon: Users,
      title: c('vorteil3_title', 'Kleine Gruppen'),
      description: c('vorteil3_desc', 'Intensive Betreuung durch erfahrene Fahrlehrer in kleinen Gruppen für optimalen Lernerfolg.')
    },
    {
      icon: Car,
      title: c('vorteil4_title', 'Moderne Fahrzeuge'),
      description: c('vorteil4_desc', 'Training auf aktuellen Fahrzeugen mit modernster Sicherheitsausstattung.')
    },
    {
      icon: Award,
      title: c('vorteil5_title', 'Hohe Erfolgsquote'),
      description: c('vorteil5_desc', 'Über 95% unserer Intensivkurs-Schüler bestehen die Prüfung beim ersten Versuch.')
    },
    {
      icon: Clock,
      title: c('vorteil6_title', 'Tägliche Fahrstunden'),
      description: c('vorteil6_desc', 'Regelmäßiges Fahren für schnellen Lernfortschritt - bis zu 2-3 Fahrstunden täglich möglich.')
    }
  ];

  const ablauf = [
    {
      step: 1,
      title: c('ablauf1_title', 'Anmeldung & Beratung'),
      description: c('ablauf1_desc', 'Persönliches Beratungsgespräch und Festlegung des Intensivkurs-Zeitraums.')
    },
    {
      step: 2,
      title: c('ablauf2_title', 'Theorieunterricht'),
      description: c('ablauf2_desc', 'Kompakter Theorieunterricht - alle 14 Pflichtthemen in wenigen Tagen.')
    },
    {
      step: 3,
      title: c('ablauf3_title', 'Praktische Ausbildung'),
      description: c('ablauf3_desc', 'Tägliche Fahrstunden mit erfahrenen Fahrlehrern. Übungsfahrten und Sonderfahrten.')
    },
    {
      step: 4,
      title: c('ablauf4_title', 'Theorieprüfung'),
      description: c('ablauf4_desc', 'Nach erfolgreichem Abschluss der Theorie folgt die Theorieprüfung beim TÜV.')
    },
    {
      step: 5,
      title: c('ablauf5_title', 'Praktische Prüfung'),
      description: c('ablauf5_desc', 'Abschließende praktische Prüfung und Erhalt des Führerscheins.')
    }
  ];

  const faqs = [
    {
      question: c('faq1_q', 'Wie lange dauert ein Intensivkurs bei Fahrschule Rößner?'),
      answer: c('faq1_a', 'Je nach Vorkenntnissen und Lernfortschritt dauert unser Intensivkurs 2-4 Wochen. Bei täglichen Fahrstunden und konzentriertem Theorieunterricht ist ein schneller Abschluss möglich.')
    },
    {
      question: c('faq2_q', 'Kann ich den Intensivkurs auch in den Ferien machen?'),
      answer: c('faq2_a', 'Ja, der Intensivkurs ist besonders beliebt in den Schul- und Semesterferien. Wir empfehlen eine frühzeitige Anmeldung, da die Plätze begrenzt sind.')
    },
    {
      question: c('faq3_q', 'Was kostet ein Führerschein-Intensivkurs?'),
      answer: c('faq3_a', 'Die Kosten entsprechen denen einer regulären Ausbildung. Der Vorteil: Durch die konzentrierte Ausbildung benötigen viele Schüler weniger Fahrstunden und sparen so Geld.')
    },
    {
      question: c('faq4_q', 'Muss ich für den Intensivkurs Urlaub nehmen?'),
      answer: c('faq4_a', 'Für einen optimalen Intensivkurs empfehlen wir, sich 2-4 Wochen freizunehmen. Wir bieten aber auch flexible Modelle für Berufstätige an.')
    },
    {
      question: c('faq5_q', 'In welchen Städten bietet Fahrschule Rößner Intensivkurse an?'),
      answer: c('faq5_a', 'Wir bieten Intensivkurse in Frankfurt (Schwanheim & Niederrad), Offenbach am Main und Bad Homburg an. Auch Schüler aus Oberursel, Eschborn und dem gesamten Rhein-Main-Gebiet sind willkommen.')
    },
    {
      question: c('faq6_q', 'Wie viele Fahrstunden brauche ich im Intensivkurs?'),
      answer: c('faq6_a', 'Die Anzahl der benötigten Fahrstunden ist individuell. Pflicht sind 12 Sonderfahrten. Die Übungsfahrten richten sich nach Ihrem Lernfortschritt - im Schnitt 20-30 Stunden.')
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Schnell zum Führerschein
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {c('hero_title', 'Führerschein')} <span className="text-blue-400">{c('hero_highlight', 'Intensivkurs')}</span>
            </h1>
            
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8" dangerouslySetInnerHTML={{ __html: c('hero_subtitle', 'In nur 2-4 Wochen zum Führerschein Klasse B. Intensivkurse in <strong>Frankfurt</strong>, <strong>Offenbach</strong> und <strong>Bad Homburg</strong> - mit erfahrenen Fahrlehrern und modernen Fahrzeugen.') }} />
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
                <Button 
                  type="submit"
                  size="lg" 
                  className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 rounded-full px-8 font-semibold"
                >
                  Jetzt Intensivkurs anfragen <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
              <a href="tel:+496920326059">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  069 20 32 60 59
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Vorteile Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {c('vorteile_title', 'Ihre Vorteile beim Intensivkurs')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {c('vorteile_subtitle', 'Mit unserem Intensivkurs erreichen Sie Ihr Ziel schneller - ohne Kompromisse bei der Qualität.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vorteile.map((vorteil, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                    <vorteil.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{vorteil.title}</h3>
                  <p className="text-slate-600">{vorteil.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {c('ablauf_title', 'So läuft der Intensivkurs ab')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {c('ablauf_subtitle', 'Von der Anmeldung bis zum Führerschein - wir begleiten Sie auf dem gesamten Weg.')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200 hidden md:block"></div>
              
              {ablauf.map((schritt, index) => (
                <div key={index} className="relative flex gap-6 mb-8 last:mb-0">
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-2xl font-bold z-10">
                    {schritt.step}
                  </div>
                  <Card className="flex-1 border-0 shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{schritt.title}</h3>
                      <p className="text-slate-600">{schritt.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Standorte Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {c('standorte_title', 'Intensivkurs in Ihrer Nähe')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {c('standorte_subtitle', 'Fahrschule Rößner bietet Intensivkurse an 4 Standorten im Rhein-Main-Gebiet.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {standorte.map((standort, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        Intensivkurs {standort.name}
                      </h3>
                      <p className="text-slate-600 mb-3">{standort.address}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {standort.keywords.slice(0, 2).map((keyword, i) => (
                          <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {keyword}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-3">
                        <a href={`tel:${standort.phone.replace(/\s/g, '')}`}>
                          <Button variant="outline" size="sm">
                            <Phone className="w-4 h-4 mr-2" />
                            {standort.phone}
                          </Button>
                        </a>
                        <Link to={`/standorte/${standort.slug}`}>
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-500">
                            Mehr Info
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-4" dangerouslySetInnerHTML={{ __html: c('erreichbar_text', 'Auch erreichbar aus: <strong>Oberursel</strong>, <strong>Eschborn</strong>, <strong>Dreieich</strong>, <strong>Neu-Isenburg</strong>, <strong>Dietzenbach</strong> und dem gesamten Rhein-Main-Gebiet.') }} />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {c('faq_title', 'Häufige Fragen zum Intensivkurs')}
            </h2>
            <p className="text-lg text-slate-600">
              {c('faq_subtitle', 'Alles was Sie über unseren Führerschein-Intensivkurs wissen müssen.')}
            </p>
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-blue-600">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-medium mb-6">
            <Star className="w-4 h-4" />
            Top-bewertet auf Google
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {c('cta_title', 'Jetzt Intensivkurs starten!')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {c('cta_subtitle', 'Sichern Sie sich Ihren Platz im nächsten Intensivkurs. Begrenzte Teilnehmerzahl - jetzt anfragen!')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <form method="POST" target="_blank" action="https://api.fahrschulmanager.de/v1/onlineanmeldung/authenticate?t=8jT3GKqjQY4PQILDGcBblEvr%2F1EfNxqBcLlN0OrkAW7dww5L6e3pxd0HUN4PrKGX0CBTr0K7Qk3BrHvIWwm%2BAQ%3D%3D">
              <Button 
                type="submit"
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8"
              >
                Jetzt anmelden <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
            <a href="tel:+496920326059">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Kostenlos beraten lassen
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IntensivkursPage;
